<template>
  <div ref="menuRoot" class="relative inline-block text-left">
    <!-- 已登录状态：显示用户头像和下拉菜单 -->
    <template v-if="userStore.isLoggedIn">
      <button
        ref="menuTrigger"
        @click="toggleMenu"
        class="user-menu-trigger ml-2 flex items-center space-x-2 focus:outline-none focus:ring-0 transition-all duration-300 ease-in-out"
      >
        <div class="user-avatar w-9 h-9 rounded-full flex items-center justify-center text-white text-lg font-semibold shadow-md transition-all duration-300 hover:shadow-lg">
          {{ userInitial }}
        </div>
        <svg
          :class="{'rotate-180': isMenuOpen}"
          class="w-3 h-3 transition-transform duration-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    
      <!-- 下拉菜单 -->
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div
          v-show="isMenuOpen"
          class="menu-panel absolute right-0 mt-4 w-56 origin-top-right rounded-lg focus:outline-none z-[100]"
        >
          <div class="menu-section-divider px-4 py-3 text-sm menu-email">
            <p class="font-semibold truncate">{{ userStore.userInfo.email }}</p>
          </div>
          <div class="py-1">
            <button
              v-for="(item, index) in menuItems"
              :key="index"
              type="button"
              :disabled="item.disabled"
              @click="item.disabled ? null : item.action()"
              class="w-full text-left px-4 py-2 text-sm transition-colors duration-200 flex items-center space-x-2"
              :class="[
                item.disabled
                  ? 'menu-item-disabled cursor-not-allowed'
                  : 'menu-item-active'
              ]"
            >
              <i :class="item.icon" class="menu-item-icon" aria-hidden="true"></i>
              <span>{{ item.isRaw ? item.label : t(item.label) }}</span>
            </button>
            <div class="menu-theme-group">
              <button
                type="button"
                class="w-full text-left px-4 py-2 text-sm transition-colors duration-200 flex items-center justify-between menu-item-active"
                @click="toggleThemeOptions"
              >
                <span class="flex items-center space-x-2">
                  <i class="pi pi-palette menu-item-icon" aria-hidden="true"></i>
                  <span>{{ t('header.userMenu.themeMode') }}</span>
                </span>
                <span class="theme-mode-current flex items-center space-x-1">
                  <span>{{ currentThemeModeLabel }}</span>
                  <i
                    :class="{'rotate-180': isThemeOptionsOpen}"
                    class="pi pi-chevron-down theme-mode-chevron transition-transform duration-200"
                    aria-hidden="true"
                  ></i>
                </span>
              </button>
              <div v-show="isThemeOptionsOpen" class="theme-mode-options py-1">
                <button
                  v-for="option in themeModeOptions"
                  :key="option.value"
                  type="button"
                  class="theme-mode-option w-full px-4 py-2 text-sm transition-colors duration-200 flex items-center space-x-2"
                  :class="currentThemeMode === option.value ? 'theme-mode-option-active' : 'theme-mode-option-inactive'"
                  @click="setThemeMode(option.value)"
                >
                  <i :class="option.icon" class="menu-item-icon" aria-hidden="true"></i>
                  <span>{{ t(option.label) }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </template>

    <!-- 未登录状态：显示登录按钮 -->
    <button
      v-else
      @click="navigateToLogin"
      class="user-login-button px-4 py-2 text-sm font-medium border rounded-full transition-all duration-300 ease-in-out transform hover:scale-105 focus:outline-none"
    >
      {{ t('header.userMenu.login') }}
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/core/store/useUserStore';
import { useAuthAnalytics } from '@/core/analytics/hooks/useAuthAnalytics';
import { useNavigationAnalytics } from '@/core/analytics/hooks/useNavigationAnalytics';
import { useI18n } from 'vue-i18n';
import { THEME_MODES, applyThemeMode, getStoredThemeMode } from '@/composables/useThemeMode';

const router = useRouter();
const userStore = useUserStore();
const isMenuOpen = ref(false);
const isThemeOptionsOpen = ref(false);
const menuRoot = ref(null);
const menuTrigger = ref(null);
const currentThemeMode = ref(getStoredThemeMode());
const { t } = useI18n();
const authAnalytics = useAuthAnalytics();
const navigationAnalytics = useNavigationAnalytics();

const userInitial = computed(() => {
  const email = userStore.userInfo.email;
  return email ? email[0].toUpperCase() : '?';
});

const themeModeOptions = computed(() => ([
  { value: THEME_MODES.LIGHT, label: 'header.userMenu.themeLight', icon: 'pi pi-sun' },
  { value: THEME_MODES.DARK, label: 'header.userMenu.themeDark', icon: 'pi pi-moon' },
  { value: THEME_MODES.SYSTEM, label: 'header.userMenu.themeSystem', icon: 'pi pi-desktop' }
]));

const currentThemeModeLabel = computed(() => {
  const currentOption = themeModeOptions.value.find(option => option.value === currentThemeMode.value);
  return currentOption ? t(currentOption.label) : '';
});

// 修改 toggleMenu 函数
const toggleMenu = () => {
  const nextState = !isMenuOpen.value;
  isMenuOpen.value = nextState;

  authAnalytics.trackAuthUserMenuToggled({
    menuState: nextState ? 'open' : 'closed',
    reasonCode: 'user_toggle',
    elementName: 'user_avatar_menu',
    elementType: 'menu'
  });

  if (!nextState) {
    menuTrigger.value?.blur();
  }
};

const closeMenu = (e) => {
  if (!menuRoot.value || !(e.target instanceof Node)) {
    return;
  }

  if (!menuRoot.value.contains(e.target) && isMenuOpen.value) {
    authAnalytics.trackAuthUserMenuToggled({
      menuState: 'closed',
      reasonCode: 'outside_click',
      elementName: 'user_avatar_menu',
      elementType: 'menu'
    });
    isMenuOpen.value = false;
    menuTrigger.value?.blur();
  }
};

const handleLogout = async () => {
  authAnalytics.trackAuthLogoutStarted({
    sourcePosition: 'user_menu',
    authFlow: 'logout',
    elementName: 'logout_button',
    elementType: 'menu_item'
  });

  try {
    const result = await userStore.logout();
    // 只接受显式 { success: true }；不兼容模糊返回值（如 undefined / {success:false}），
    // 否则真实 store 的常规失败路径 {success:false, error} 会被误报为成功。
    if (result && result.success === true) {
      authAnalytics.trackAuthLogoutCompleted({
        actionStatus: 'success',
        redirectTarget: 'login',
        authFlow: 'logout'
      });
      isMenuOpen.value = false;
      const currentLang = router.currentRoute.value.params.lang;
      router.push(`/${currentLang}/login`);
      return;
    }
    // resolve 但未成功（如 {success:false, error}）：失败、不导航、不上传 error 原文。
    authAnalytics.trackAuthLogoutCompleted({
      actionStatus: 'failed',
      reasonCode: 'logout_failed',
      authFlow: 'logout'
    });
    isMenuOpen.value = false;
  } catch (error) {
    authAnalytics.trackAuthLogoutCompleted({
      actionStatus: 'failed',
      reasonCode: 'logout_failed',
      errorCode: error?.code || error?.name,
      authFlow: 'logout'
    });
    isMenuOpen.value = false;
  }
};

const normalizeRedirectTarget = (redirect) => {
  if (!redirect || typeof redirect !== 'string') return 'upload';
  const normalized = redirect.replace(/^\/[a-zA-Z-]+/, '');
  if (!normalized || normalized === '/') return 'home';
  if (normalized.startsWith('/upload')) return 'upload';
  if (normalized.startsWith('/result')) return 'result';
  if (normalized.startsWith('/dashboard')) return 'dashboard';
  if (normalized.startsWith('/history')) return 'history';
  if (normalized.startsWith('/login')) return 'login';
  return 'external_or_unknown';
};

const navigateToLogin = () => {
  const currentLang = router.currentRoute.value.params.lang;
  const redirect = router.currentRoute.value.query.redirect;
  const redirectTarget = normalizeRedirectTarget(redirect);

  authAnalytics.trackAuthEntryClicked({
    sourcePosition: 'header_nav',
    elementName: 'login_button',
    elementType: 'button',
    redirectTarget,
    redirectPresent: !!redirect
  });

  router.push(`/${currentLang}/login`);
};

const getCurrentLang = () => router.currentRoute.value.params.lang;

const trackUserMenuNavigation = (targetRouteName, elementName) => {
  navigationAnalytics.trackAppNavigationClicked({
    sourcePosition: 'user_menu',
    eventLabel: `navigate_${targetRouteName}`,
    elementName,
    elementType: 'menu_item',
    targetRouteName,
    targetRouteType: 'internal',
    method: 'user_click',
    lang: getCurrentLang(),
    isLoggedIn: true
  });
};

const setThemeMode = (mode) => {
  const previousMode = currentThemeMode.value;
  if (previousMode === mode) {
    menuTrigger.value?.blur();
    return;
  }

  const { mode: appliedMode } = applyThemeMode(mode);
  currentThemeMode.value = appliedMode;

  navigationAnalytics.trackUiPreferenceChanged({
    sourcePosition: 'user_menu_theme_mode',
    eventLabel: 'theme_mode_changed',
    elementName: `theme_mode_${mode}`,
    elementType: 'menu_item',
    optionName: 'theme_mode',
    optionValue: mode,
    previousValue: previousMode,
    method: 'user_click',
    lang: getCurrentLang(),
    isLoggedIn: true
  });

  menuTrigger.value?.blur();
};

const toggleThemeOptions = () => {
  isThemeOptionsOpen.value = !isThemeOptionsOpen.value;
};

const navigateToHistory = () => {
  const currentLang = getCurrentLang();
  trackUserMenuNavigation('history', 'history_menu_item');
  router.push(`/${currentLang}/history`);
  isMenuOpen.value = false;
};

const navigateToDashboard = () => {
  const currentLang = getCurrentLang();
  trackUserMenuNavigation('dashboard', 'dashboard_menu_item');
  router.push(`/${currentLang}/dashboard`);
  isMenuOpen.value = false;
};

const menuItems = computed(() => ([
  { label: 'header.userMenu.dashboard', icon: 'pi pi-th-large', action: navigateToDashboard },
  { label: 'header.userMenu.history', icon: 'pi pi-history', action: navigateToHistory },
  { label: 'header.userMenu.logout', icon: 'pi pi-sign-out', action: handleLogout }
]));

onMounted(() => {
  document.addEventListener('pointerdown', closeMenu, true);
  userStore.checkAuth();
  currentThemeMode.value = getStoredThemeMode();
});

onUnmounted(() => {
  document.removeEventListener('pointerdown', closeMenu, true);
});
</script>

<style scoped>
.relative {
  isolation: isolate;
}

.user-menu-trigger {
  color: var(--text-secondary);
}

.user-menu-trigger:hover {
  color: var(--theme-primary);
}

.user-avatar {
  background-image: linear-gradient(to right, var(--theme-gradient-start), var(--theme-gradient-end));
}

.user-login-button {
  color: var(--theme-primary);
  border-color: var(--theme-primary);
  background-color: var(--button-secondary-bg);
}

.user-login-button:hover {
  background-color: color-mix(in srgb, var(--theme-gradient-start) 7%, var(--surface-card));
}

.user-login-button:focus-visible {
  box-shadow: 0 0 0 2px var(--theme-focus-ring);
}

.theme-mode-option-inactive {
  color: var(--text-secondary);
  background-color: transparent;
}

.theme-mode-option-inactive:hover {
  background-color: var(--button-secondary-hover);
}

.theme-mode-option-active {
  color: var(--theme-primary);
  background-color: color-mix(in srgb, var(--theme-primary-soft-bg) 70%, transparent);
}

.theme-mode-current {
  color: var(--text-muted);
  font-size: 0.75rem;
}

.theme-mode-chevron {
  font-size: 0.7rem;
}

.theme-mode-options {
  background-color: color-mix(in srgb, var(--surface-card) 72%, transparent);
}

.menu-item-icon {
  width: 1rem;
  flex-shrink: 0;
  text-align: center;
  font-size: 0.9rem;
}

.menu-panel {
  background-color: var(--surface-elevated);
  border: 1px solid color-mix(in srgb, var(--border-default) 90%, transparent);
}

.menu-email {
  color: var(--text-secondary);
}

.menu-section-divider {
  border-bottom: 1px solid var(--border-default);
}

.menu-item-active {
  color: var(--text-secondary);
}

.menu-item-active:hover {
  background-color: var(--button-secondary-hover);
}

.menu-item-disabled {
  color: var(--text-muted);
}

/* 更新下拉菜单样式 */
.absolute {
  z-index: 9999;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  box-shadow: 0 12px 28px color-mix(in srgb, var(--shadow-color) 75%, transparent);
}
</style>
